import Spinner from "@/components/spinner";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useCompanyById } from "@/hooks/company-by-id.hook";
import { useCurrentUserProfile } from "@/hooks/current-user-profile.hook";
import { useUpdateCompanyInfo } from "@/hooks/update-company-info.hook";
import { GetCompanyByIdResponse } from "@/requests/companies/company-by-id.request";
import { zodResolver } from "@hookform/resolvers/zod";
import { Edit2 } from "iconsax-react";
import { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { formSchema } from "./company-form.schema";
import CompanyHeading from "./company-heading";

const resetForm = (
  form: ReturnType<typeof useForm<z.infer<typeof formSchema>>>,
  company: GetCompanyByIdResponse,
) => {
  form.reset({
    taxId: company.taxId || "",
    address: company.address.street || "",
    tradeName: company.tradeName || "",
    website: company.website || "",
    mainOffering: company.principalProduct || "",
    startYearOfOperations: company.yearOfOperationsStart
      ? Number(company.yearOfOperationsStart)
      : "",
  });
};

const CompanyForm: FC = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      taxId: "",
      address: "",
      tradeName: "",
      website: "",
      mainOffering: "",
      startYearOfOperations: "",
    },
  });

  const { data: userProfile } = useCurrentUserProfile();

  const { data: company } = useCompanyById(userProfile?.companyId || "");

  const mutation = useUpdateCompanyInfo();

  useEffect(() => {
    if (company) {
      resetForm(form, company);
    }
  }, [company]);

  useEffect(() => {
    if (mutation.isSuccess) {
      setIsOpen(false);
    }
  }, [mutation.isSuccess]);

  function onSubmit(values: z.infer<typeof formSchema>) {
    mutation.mutate({
      companyId: company!.id,
      taxId: values.taxId,
      address: {
        street: values.address,
      },
      tradeName: values.tradeName,
      website: values.website,
      principalProduct: values.mainOffering,
      startingYear: Number(values.startYearOfOperations),
    });
  }

  const handleCancel = () => {
    resetForm(form, company!);
    setIsOpen(false);
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button>
          <Edit2 /> Editar
        </Button>
      </SheetTrigger>

      <SheetContent className="flex flex-col gap-0">
        <SheetHeader>
          <SheetTitle>Editar empresa</SheetTitle>
          <SheetDescription className="hidden">
            Actualiza la información de tu empresa
          </SheetDescription>
        </SheetHeader>
        <SheetBody>
          <CompanyHeading companyName={company!.name} className="mb-8 mt-4" />

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex flex-col gap-6"
            >
              <h3 className="text-base font-bold text-[#615CB7]">
                Información general
              </h3>

              <FormField
                control={form.control}
                name="taxId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>RUC</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Dirección</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="tradeName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nombre comercial (trofeo)</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="website"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Sitio web</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <h3 className="text-base font-bold text-[#615CB7]">
                Descripción
              </h3>

              <FormField
                control={form.control}
                name="mainOffering"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Principal producto/servicio</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="startYearOfOperations"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Año de inicio de operaciones</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="sticky bottom-0 mt-6 grid grid-cols-2 items-center gap-2 bg-white pb-4 pt-2">
                <Button
                  type="submit"
                  variant="default"
                  disabled={mutation.isPending}
                >
                  {mutation.isPending && <Spinner className="mr-2" />}
                  Guardar cambios
                </Button>

                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleCancel}
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </Form>
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
};

export default CompanyForm;
